import { Component, inject, OnInit } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';
import { BathroomService } from 'src/app/services/configurators-2d/applications/bathroom.service';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { ModalService, MODAL } from 'src/app/services/modal.service';
import { BathroomConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/bathroom-configuration-data';
import { InstallationState, PricingState } from 'src/app/stores/tunnel/model/tunnel-data-state';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-casto-bathroom-tunnel-installation',
  templateUrl: './bathroom-tunnel-installation.component.html',
})
export class BathroomTunnelInstallationComponent extends BaseTunnelObject implements OnInit {
  // Services
  protected bathroomService = inject(BathroomService);
  protected modalService = inject(ModalService);
  protected featureSettingsService = inject(FeatureSettingsService);

  // Properties
  protected currentInstallation: InstallationState = {
    withInstallation: true,
    more2Years: undefined,
    detailInstallation: undefined,
  };
  private bathroomConfiguration: BathroomConfigurationData;
  protected currentDiscount: DiscountTypeEnum;
  protected currentPricing: PricingState;
  protected loading = false;
  protected pricingLoading: boolean = false;
  protected more2Years: boolean = undefined;
  protected more2YearsOptions: any[] = [
    { label: 'yes', value: true },
    { label: 'no', value: false },
  ];
  private initInstallationLoading = false;

  constructor() {
    const tunnelNavigationStore = inject(TunnelNavigationStore);
    const tunnelDataStore = inject(TunnelDataStore);
    super(tunnelNavigationStore, tunnelDataStore);

    this.tunnelNavigationStore = tunnelNavigationStore;
    this.tunnelDataStore = tunnelDataStore;
  }

  async ngOnInit(): Promise<void> {
    if (!(await this.featureSettingsService.isPoseEnabled())) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    super.ngOnInit();
  }

  get displayDiscount(): boolean {
    return this.discountNumberValue > 0;
  }

  get discountNumberValue(): number {
    switch (this.currentDiscount) {
      case DiscountTypeEnum.Discount0:
        return 0;
      case DiscountTypeEnum.Discount5:
        return 5;
      case DiscountTypeEnum.Discount10:
        return 10;
      case DiscountTypeEnum.Discount20:
        return 20;
    }
  }

  private async initInstallation() {
    if (this.initInstallationLoading) {
      return;
    }

    try {
      this.initInstallationLoading = true;
      const poseEnabled = await this.featureSettingsService.isPoseEnabled();
      await this.setWithInstallation(poseEnabled, true);
    } catch (error) {
      logError(error);
    } finally {
      this.initInstallationLoading = false;
    }
  }

  private shouldComputeInstallation(newCurrentInstallation: InstallationState): boolean {
    return (
      newCurrentInstallation.withInstallation != this.currentInstallation?.withInstallation &&
      !this.loading &&
      !(
        this.currentInstallation.detailInstallation === undefined &&
        newCurrentInstallation.detailInstallation !== undefined
      )
    );
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getCurrentInstallation().subscribe(async currentInstallation => {
        if (currentInstallation) {
          const poseEnabled = await this.featureSettingsService.isPoseEnabled();

          if (!poseEnabled && currentInstallation.withInstallation !== false) {
            await this.setWithInstallation(false, true);
            return;
          }
          if (this.shouldComputeInstallation(currentInstallation)) {
            await this.computeInstallation();
          }
          if (!this.loading) {
            this.more2Years = currentInstallation.more2Years;
            this.currentInstallation.withInstallation = currentInstallation.withInstallation;
            this.currentInstallation.more2Years = currentInstallation.more2Years;
          }
          this.currentInstallation.detailInstallation = currentInstallation.detailInstallation;
        } else {
          await this.initInstallation();
        }
      }),
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),
      this.tunnelDataStore.getCurrentDiscount().subscribe(async currentDiscount => {
        if (currentDiscount && this.currentDiscount !== currentDiscount) {
          this.currentDiscount = currentDiscount;
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.bathroomConfiguration = configurationData.get(CONFIG_CONTEXT.BATHROOM_V2) as BathroomConfigurationData;
        }
      }),
      this.tunnelDataStore.getPricingLoading().subscribe(pricingLoading => {
        this.pricingLoading = pricingLoading;
      })
    );
  }

  private async computeInstallation() {
    try {
      this.loading = true;
      await this.bathroomService.computeInstallation();
    } catch (error) {
      logError(error);
    } finally {
      this.loading = false;
    }
  }

  protected async setMore2Years(more2Years: boolean): Promise<void> {
    if (more2Years === null) {
      return;
    }
    this.loading = true;
    this.currentInstallation.more2Years = more2Years;
    this.tunnelDataStore.setMoreThan2Years(more2Years);
    this.tunnelDataStore.resetCurrentFunding();
    await this.updateInstallation();
    this.loading = false;
  }

  private async updateInstallation() {
    await this.computeInstallation();
  }

  protected async setWithInstallation(withInstallation: boolean, force: boolean = false): Promise<void> {
    if (force || this.currentInstallation.withInstallation !== withInstallation) {
      this.loading = true;
      this.currentInstallation.withInstallation = withInstallation;
      this.tunnelDataStore.setWithInstallation(withInstallation);
      this.tunnelDataStore.setMoreThan2Years(this.more2Years);
      this.tunnelDataStore.resetCurrentFunding();
      await this.bathroomService.updateWithInstallation(withInstallation);
      await this.computeInstallation();
      this.loading = false;
    }
  }

  openDetail() {
    this.modalService.display(MODAL.INSTALLATION_DETAIL, {
      installationDetails: this.currentInstallation.detailInstallation,
      bom: this.bathroomConfiguration?.products,
    });
  }
}
